/**
 * The business's paper, as the web draws it.
 *
 * The same layout the PDF prints — logo top left, address, email and ABN top
 * right, the number and date beneath — so the builder's canvas, the customer's
 * page and the Settings preview all look like the document that gets sent.
 * Colours come from `proposalTheme` as CSS variables on the surface, which is
 * how a Tailwind class can say `text-[var(--p-brand)]`. Decision 81.
 */
import { letterheadLines, proposalHeading, proposalTheme, type ProposalTheme } from '@fieldops/shared'
import type { CSSProperties } from 'react'
import { cn } from '../../lib/cn.js'
import { logoSrc } from '../../lib/files.js'
import './fonts.js'

export interface LetterheadBusiness {
  name: string
  address?: string | null
  phone?: string | null
  email?: string | null
  website?: string | null
  taxNumber?: string | null
  logoUrl?: string | null
  brandColour?: string | null
  accentColour?: string | null
}

/** The palette as custom properties, for a proposal surface's root. */
export function themeVars(theme: ProposalTheme): CSSProperties {
  return {
    '--p-brand': theme.brand,
    '--p-brand-text': theme.brandText,
    '--p-brand-soft': theme.brandSoft,
    '--p-on-brand': theme.onBrand,
    '--p-accent': theme.accent,
    '--p-accent-text': theme.accentText,
    '--p-accent-soft': theme.accentSoft,
    '--p-on-accent': theme.onAccent,
  } as CSSProperties
}

export function themeOf(business: LetterheadBusiness | null | undefined): ProposalTheme {
  return proposalTheme(business ?? {})
}

export function Letterhead({
  business,
  number,
  date,
  compact = false,
  className,
}: {
  business: LetterheadBusiness
  number?: string | null
  date?: string | null
  compact?: boolean
  className?: string
}) {
  const logo = logoSrc(business.logoUrl)
  const lines = letterheadLines(business)

  return (
    <header className={cn('font-proposal', className)}>
      <div className="flex items-start justify-between gap-6">
        <img
          src={logo}
          alt={business.name}
          className={cn('w-auto max-w-[55%] object-contain object-left', compact ? 'h-10' : 'h-14 sm:h-16')}
        />
        {lines.length > 0 ? (
          <address
            className={cn(
              'text-right not-italic text-[var(--p-accent-text)]',
              compact ? 'text-[10px] leading-4' : 'text-xs leading-5 sm:text-[13px]',
            )}
          >
            {lines.map((line) => (
              <span key={line} className="block">
                {line}
              </span>
            ))}
          </address>
        ) : null}
      </div>
      {number || date ? (
        <div className={cn('text-right', compact ? 'mt-2 text-[10px]' : 'mt-4 text-xs sm:text-[13px]')}>
          {number ? <p className="font-bold text-ink-900">{proposalHeading(number)}</p> : null}
          {date ? <p className="text-ink-700">{date}</p> : null}
        </div>
      ) : null}
    </header>
  )
}
